/**
 * What a recipe still needs before it can be published, as the UI sees it
 * (T-704).
 *
 * The rule itself lives on the API — `recipe-requirements.ts` — and travels
 * with the recipe as `missingForPublish`. This module holds only what the
 * server has no business knowing: which input each requirement belongs to, and
 * what to call it on screen. Deriving the answer here too is how the banner
 * came to say "Missing Information" about a recipe with nothing missing.
 */

import { useLingui } from "@lingui/react/macro";
import type { Recipe } from "@/types/recipe";

export const PUBLISH_REQUIREMENTS = [
  "yield",
  "ingredients",
  "instructions",
] as const;

export type PublishRequirement = (typeof PUBLISH_REQUIREMENTS)[number];

/**
 * The form fields each requirement covers, so an input can be marked required
 * without asking whether it is currently filled in.
 */
export const REQUIREMENT_FIELDS: Record<PublishRequirement, readonly string[]> = {
  yield: ["yieldQuantity", "yieldUom"],
  ingredients: ["ingredients"],
  instructions: ["instructions"],
};

/** The tab a cook has to open to deal with each one. */
export const REQUIREMENT_TAB: Record<PublishRequirement, string> = {
  yield: "general",
  ingredients: "ingredients",
  instructions: "execution",
};

function isPublishRequirement(value: unknown): value is PublishRequirement {
  return (PUBLISH_REQUIREMENTS as readonly unknown[]).includes(value);
}

/**
 * What the server says is missing. An older API that does not send the list at
 * all reads as nothing missing rather than as everything missing — a recipe is
 * not incomplete just because we could not ask.
 */
export function missingForPublish(recipe: Pick<Recipe, "missingForPublish">): PublishRequirement[] {
  return (recipe.missingForPublish ?? []).filter(isPublishRequirement);
}

/** Every requirement that lands on a given tab. */
export function missingOnTab(missing: PublishRequirement[], tab: string): PublishRequirement[] {
  return missing.filter((requirement) => REQUIREMENT_TAB[requirement] === tab);
}

/**
 * Which tab a rejected save belongs on. The API keys its refusals by field
 * name, so work back through REQUIREMENT_FIELDS rather than keeping a second
 * map that could drift from it (T-705).
 */
export function tabForFields(fields: Record<string, unknown>): string | null {
  const names = Object.keys(fields);

  if (names.includes("portionQuantity") || names.includes("portionUom")) return "general";

  for (const requirement of PUBLISH_REQUIREMENTS) {
    if (REQUIREMENT_FIELDS[requirement].some((field) => names.includes(field))) {
      return REQUIREMENT_TAB[requirement];
    }
  }

  return null;
}

/** What each requirement is called on screen — the banner lists these. */
export function useRequirementLabels(): Record<PublishRequirement, string> {
  const { t } = useLingui();

  return {
    yield: t`Total yield`,
    ingredients: t`Ingredients`,
    instructions: t`Steps`,
  };
}

/** The same requirements as an instruction, for the note under an input. */
export function useRequirementNotes(): Record<PublishRequirement, string> {
  const { t } = useLingui();

  return {
    yield: t`Needed to publish: how much one batch makes, and in what unit.`,
    ingredients: t`Needed to publish: add at least one ingredient.`,
    instructions: t`Needed to publish: add at least one step.`,
  };
}
